

import { useState, useEffect, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { quotationsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/utils";
import StatusUpdatePopup from "../StatusUpdatePopup";
import ConvertToEventModal, { ConvertEventData } from "../ConvertToEventModal";
import PrintableQuotation from "../components/PrintableQuotation";
import {
  QuotationDetailContent,
  QuotationNotFound,
  QuotationLoading,
} from "../components";
import { useQuotationModals } from "../hooks/useQuotationModals";

interface Quotation {
  id: number;
  quotation_number: string;
  client_id: number;
  client?: { id: number; name: string; email?: string; phone?: string };
  event_name?: string;
  event_type?: string;
  guests?: number;
  guest_count?: number;
  event_date: string;
  package_id?: number;
  package?: { id: number; name: string };
  venue?: string;
  venue_id?: number;
  items?: any[];
  quotation_services?: any[];
  vendors_data?: any[];
  quotation_vendors?: any[];
  subtotal: number;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  tax: number;
  total: number;
  status: string;
  valid_until: string;
  notes: string;
}

function QuotationDetailPageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { settings } = useSettings();
  const logoUrl = settings?.logoUrl ? getImageUrl(settings.logoUrl) : undefined;
  const businessName = settings?.business?.businessName || 'Shalimar';
  const isStaff = user?.role === 'staff';
  
  // Ensure searchParams is accessed only within client component
  if (!searchParams) return null;

  const quotationId = searchParams.get("id");
  const editMode = searchParams.get("edit") === "true";

  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [printQuotation, setPrintQuotation] = useState<Quotation | null>(null);
  const {
    isStatusPopupOpen,
    isConvertModalOpen,
    isConvertLoading,
    convertError,
    handleUpdateStatus,
    handleConfirmConvert,
    openStatusPopup,
    closeStatusPopup,
    openConvertModal,
    closeConvertModal,
  } = useQuotationModals(quotation!);

  useEffect(() => {
    if (!quotationId) {
      setError("No quotation ID provided");
      setLoading(false);
      return;
    }

    // Validate that quotation ID is a valid number
    const id = parseInt(quotationId);
    if (isNaN(id) || id <= 0) {
      setError("Invalid quotation ID");
      setLoading(false);
      return;
    }

    const loadQuotationDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await quotationsApi.get(parseInt(quotationId));
        const data = response.data?.data || response.data;

        // Check if quotation exists and ID matches
        if (!data || !data.id) {
          setError("Quotation not found");
          setQuotation(null);
          return;
        }

        // Verify the returned quotation ID matches the requested ID
        if (data.id !== parseInt(quotationId)) {
          setError("Quotation ID mismatch");
          setQuotation(null);
          return;
        }

        setQuotation(data);

        // If in edit mode, redirect to the quotations page with edit modal
        if (editMode && data.id) {
          navigate(`/quotations?edit=${data.id}`);
          return;
        }
      } catch (err: any) {
        // Handle different types of errors
        if (err.response?.status === 404) {
          setError("Quotation not found");
        } else if (err.response?.status === 403) {
          setError("You don't have permission to view this quotation");
        } else if (err.response?.status === 401) {
          setError("Please login to view quotation details");
        } else {
          const message = err instanceof Error ? err.message : "Failed to load quotation details";
          setError(message);
          showToast(message, "error");
        }
        setQuotation(null);
      } finally {
        setLoading(false);
      }
    };

    loadQuotationDetails();
  }, [quotationId, showToast, editMode, navigate]);

  // Handle print events
  useEffect(() => {
    const handleAfterPrint = () => {
      if (printQuotation) {
        showToast("Quotation ready - use browser's save as PDF option", "success");
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printQuotation, showToast]);

  const handleEdit = () => {
    if (!quotation) return;
    navigate(`/quotations?edit=${quotation.id}`);
  };

  const handleDownloadPDF = () => {
    if (!quotation) return;
    setPrintQuotation(quotation);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintQuotation(null), 100);
    }, 100);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <QuotationLoading />
      </DashboardLayout>
    );
  }

  if (error || !quotation) {
    return (
      <DashboardLayout>
        <QuotationNotFound error={error || undefined} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <QuotationDetailContent
        quotation={quotation}
        logoUrl={logoUrl || undefined}
        businessName={businessName}
        isStaff={isStaff}
        onDownloadPDF={handleDownloadPDF}
        onUpdateStatus={openStatusPopup}
        onConvertToEvent={openConvertModal}
        onEdit={handleEdit}
        onBack={() => navigate(-1)}
      />

      {/* Status Update Popup */}
      <StatusUpdatePopup
        isOpen={isStatusPopupOpen}
        onClose={closeStatusPopup}
        currentStatus={quotation.status || "draft"}
        quotationId={quotation.id || 0}
        quotationNumber={quotation.quotation_number || ""}
        onUpdateStatus={handleUpdateStatus}
      />

      {/* Convert to Event Modal */}
      <ConvertToEventModal
        isOpen={isConvertModalOpen}
        onClose={closeConvertModal}
        quotation={quotation as any}
        onConfirm={handleConfirmConvert}
        isLoading={isConvertLoading}
        error={convertError}
      />

      {/* Printable Quotation */}
      {printQuotation && (
        <PrintableQuotation
          quotation={printQuotation}
          businessInfo={{
            name: businessName,
            logoUrl,
            tagline: settings?.business?.tagline,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
            gstNumber: settings?.business?.gstNumber,
          }}
        />
      )}
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <QuotationLoading />
    </DashboardLayout>
  );
}

function QuotationDetailPageWrapper() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <QuotationDetailPageContent />
    </Suspense>
  );
}

export default function QuotationDetailPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <QuotationDetailPageWrapper />
    </Suspense>
  );
}
